<template>
  <section class="dvg">
    <header v-show="!sharedDataStore.isMobile()" class="dvg__head">
      <div class="dvg__head-text">
        <div class="dvg__eyebrow">
          <span class="dvg__dot"></span>
          <span class="brand-mark">WaizmannVergleich</span>&nbsp;/&nbsp;3-TARIFE-VERGLEICH
        </div>
        <h2 class="dvg__title">Vergleichen Sie 3 Tarife miteinander</h2>
        <p class="dvg__sub">
          Wählen Sie zwei weitere Tarife, um Leistungen, Begrenzungen und
          Wartezeiten direkt gegenüberzustellen.
        </p>
      </div>
    </header>

    <v-alert
      v-if="sharedDataStore.isMobile()"
      type="info"
      variant="tonal"
      density="comfortable"
      rounded="0"
      class="dvg__mobile"
      icon="mdi-phone-rotate-landscape"
    >
      Drehen Sie bitte Ihr Handy, um diesen Vergleich vollständig nutzen zu
      können.
    </v-alert>

    <div class="dvg__scroll">
      <table class="dvg__table">
        <thead>
          <tr>
            <th class="dvg__th dvg__th--label">Tarif</th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol dvg__tcol--fix">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;1</span>
                <span class="dvg__tcol-name">
                  {{ tarif1.un_name }} {{ tarif1.tarif_title }}
                </span>
                <span class="dvg__tcol-price">
                  {{ tarif1.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;2</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 2)"
                >
                  <option value selected>Tarif 2 wählen…</option>
                  <option
                    v-for="tarif2 in alletarifeFiltered"
                    :value="tarif2.tarif_id"
                    :key="tarif2.tarif_id"
                  >
                    {{ tarif2.un_name }} {{ tarif2.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif2" class="dvg__tcol-price">
                  {{ state.tarif2.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
            <th class="dvg__th dvg__th--tarif">
              <div class="dvg__tcol">
                <span class="dvg__tcol-eyebrow">Tarif&nbsp;3</span>
                <select
                  class="dvg__select"
                  @change="pushTarifToVergleich($event, 3)"
                >
                  <option value selected>Tarif 3 wählen…</option>
                  <option
                    v-for="tarif3 in alletarifeFiltered"
                    :value="tarif3.tarif_id"
                    :key="tarif3.tarif_id"
                  >
                    {{ tarif3.un_name }} {{ tarif3.tarif_title }}
                  </option>
                </select>
                <span v-if="state.tarif3" class="dvg__tcol-price">
                  {{ state.tarif3.preis_value }}&nbsp;€
                  <span class="dvg__tcol-unit">mtl.</span>
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td class="dvg__lab"><strong>Monatlicher Beitrag</strong></td>
            <td v-for="tarif in tarifs" :key="`pr-${tarif.tarif_id}`">
              <template v-if="tarif">
                <div class="dvg__price">{{ tarif.preis_value }}&nbsp;€</div>
                <div
                  v-if="tarif.empfehlung_type == 3"
                  class="dvg__badge dvg__badge--rec"
                >
                  Empfehlung „beste Leistung"
                </div>
                <div
                  v-if="
                    tarif.empfehlung_type == 1 || tarif.empfehlung_type == 2
                  "
                  class="dvg__badge"
                >
                  Alternativ-Empfehlung
                </div>
                <div v-if="tarif.zuschlag_zahn > 0" class="dvg__hint">
                  inkl. {{ tarif.zuschlag_zahn }} € Zuschlag für Lücken
                </div>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Beitragsentwicklung</strong>
              <div class="dvg__sublab">voraussichtlich</div>
            </td>
            <td v-for="tarif in tarifs" :key="`bew-${tarif.tarif_id}`">
              <template v-if="tarif">
                nächstes Jahr: <b>{{ tarif.preis_value_plus_1 }}&nbsp;€</b>
                <br />
                in 5 Jahren: <b>{{ tarif.preis_value_plus_5 }}&nbsp;€</b>
                <br />
                in 10 Jahren: <b>{{ tarif.preis_value_plus_10 }}&nbsp;€</b>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Infopaket</strong>
              <div class="dvg__sublab">kostenlos</div>
            </td>
            <td v-for="tarif in tarifs" :key="`btn-${tarif.tarif_id}`">
              <template v-if="tarif && tarif.antraglink">
                <v-btn
                  :href="tarif.antraglink"
                  variant="flat"
                  color="primary"
                  rounded="0"
                  density="comfortable"
                  size="default"
                  class="dvg__cta"
                  append-icon="mdi-arrow-right"
                >
                  Antrag & Infopaket
                  <span
                    v-if="tarif.empfehlung_type > 0"
                    class="dvg__cta-tip"
                  >
                    Tipp
                  </span>
                </v-btn>
              </template>
            </td>
          </tr>

          <tr v-if="berechnung.ber_algo_view != 2">
            <td class="dvg__lab">
              <strong>Ø-Erstattung</strong>
              <div class="dvg__sublab">WaizmannWert</div>
            </td>
            <td v-for="tarif in tarifs" :key="`ww-${tarif.tarif_id}`">
              <template v-if="tarif">
                <strong>{{ tarif.tarif_waizmannwert }}&nbsp;%</strong>
              </template>
            </td>
          </tr>
          <tr v-if="berechnung.ber_algo_view == 2">
            <td class="dvg__lab">
              <strong>Ø-Erstattung</strong>
              <div class="dvg__sublab">Kieferorthopädie</div>
            </td>
            <td v-for="tarif in tarifs" :key="`kw-${tarif.tarif_id}`">
              <template v-if="tarif">
                <strong>{{ tarif.tarif_kwert_gesamt }}&nbsp;%</strong>
              </template>
            </td>
          </tr>

          <tr v-if="berechnung.ber_sofort == 1">
            <td class="dvg__lab">
              <strong>Angeratene Behandlungen</strong>
            </td>
            <td v-for="tarif in tarifs" :key="`so-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span v-if="!tarif.sofortversichert" class="dvg__neg">
                  nicht versichert
                </span>
                <strong v-if="tarif.sofortversichert" class="dvg__pos">
                  {{ tarif.sofort_text_short }}
                </strong>
              </template>
            </td>
          </tr>

          <tr v-if="berechnung.ber_wechsel == 1">
            <td class="dvg__lab">
              <strong>Wechsel-Angebot</strong>
            </td>
            <td v-for="tarif in tarifs" :key="`we-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span v-if="tarif.tarif_wechsel_angebot == 0" class="dvg__neg">
                  keine Wechseloption
                </span>
                <strong
                  v-if="tarif.tarif_wechsel_angebot == 1"
                  class="dvg__pos"
                >
                  Sehr gute Wechseloption mit Wartezeiterlass
                </strong>
                <strong
                  v-if="tarif.tarif_wechsel_angebot == 2"
                  class="dvg__pos"
                >
                  Gute Wechseloption
                </strong>
              </template>
            </td>
          </tr>

          <tr
            v-if="
              berechnung.ber_algo_view != 2 &&
              berechnung.ber_alter > 17 &&
              berechnung.ber_luecke > 0
            "
          >
            <td class="dvg__lab">
              <strong>Fehlende Zähne</strong>
              <div class="dvg__sublab">mitversichert</div>
            </td>
            <td v-for="tarif in tarifs" :key="`lu-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span v-if="berechnung.ber_luecke == 1">
                  Ihr <strong>fehlender Zahn</strong> ist
                </span>
                <span v-if="berechnung.ber_luecke > 1">
                  Ihre
                  <strong>{{ berechnung.ber_luecke }} fehlenden Zähne</strong>
                  sind
                </span>
                <span v-if="tarif.LueckeVersicherung == 1" class="dvg__pos">
                  <strong>kostenlos mitversichert.</strong>
                </span>
                <span
                  v-if="
                    tarif.LueckeVersicherung == 2 ||
                    tarif.LueckeVersicherung == 3
                  "
                >
                  gegen einen Zuschlag von
                  <strong>{{ tarif.zuschlag_zahn }} €</strong> mitversichert.
                  Der Zuschlag ist im Monatsbeitrag von
                  {{ tarif.preis_value }} € bereits enthalten.
                </span>
                <span v-if="tarif.LueckeVersicherung == 4">
                  ohne Aufschlag mitversichert. Allerdings verlängern sich die
                  anfänglichen Begrenzungen.
                </span>
                <span v-if="tarif.LueckeVersicherung == 0" class="dvg__neg">
                  <strong>nicht mitversichert.</strong>
                </span>
              </template>
            </td>
          </tr>

          <tr class="dvg__section">
            <td :colspan="showColspan">
              <span class="dvg__section-eyebrow">Schutz</span>
              <h3 class="dvg__section-title">
                Clearment Vertragsschutz | Spezialleistungen
              </h3>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Clearment</strong>
              <div class="dvg__sublab">Zertifizierung</div>
            </td>
            <td v-for="tarif in tarifs" :key="`cl-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span
                  v-if="tarif1.tarif_show_lsk_style"
                  :class="tarif1.tarif_show_lsk_style"
                >
                  <b>{{ tarif1.tarif_show_lsk_word }}</b>
                </span>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>Clearment</strong>
              <div class="dvg__sublab">Vertragsschutz</div>
            </td>
            <td v-for="tarif in tarifs" :key="`cs-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span
                  v-if="tarif.tarif_clearment_vs && tarif.tarif_clearment_vs == 1"
                  class="dvg__pos"
                >
                  <b>ja</b>
                </span>
                <span
                  v-if="tarif.tarif_clearment_vs && tarif.tarif_clearment_vs == 0"
                  class="dvg__neg"
                >
                  nein
                </span>
              </template>
            </td>
          </tr>

          <tr>
            <td class="dvg__lab">
              <strong>AntragAnnahmeCheck</strong>
            </td>
            <td v-for="tarif in tarifs" :key="`aa-${tarif.tarif_id}`">
              <template v-if="tarif">
                <span
                  v-if="tarif.tarif_int_antrag && tarif.tarif_int_antrag == 1"
                  class="dvg__pos"
                >
                  <b>ja</b>
                </span>
                <span
                  v-if="tarif.tarif_int_antrag && tarif.tarif_int_antrag == 0"
                  class="dvg__neg"
                >
                  nein
                </span>
              </template>
            </td>
          </tr>

          <template
            v-if="berechnung.ber_algo_view != 2 || tarif1.tarif_id == 276"
          >
            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Leistungen</span>
                <h3 class="dvg__section-title">Kieferorthopädie</h3>
              </td>
            </tr>

            <tr>
              <td class="dvg__lab">
                <strong>Ø-Erstattung</strong>
                <div class="dvg__sublab">KFO KIG 1-2</div>
              </td>
              <td v-for="tarif in tarifs" :key="`k1-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_kwert_1 }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Ø-Erstattung</strong>
                <div class="dvg__sublab">KFO KIG 3-5</div>
              </td>
              <td v-for="tarif in tarifs" :key="`k3-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_kwert_3 }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>KFO Begrenzung</strong>
                <div class="dvg__sublab">KIG 1-2</div>
              </td>
              <td v-for="tarif in tarifs" :key="`kb1-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_kfo_beg_ogkv }}</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>KFO Begrenzung</strong>
                <div class="dvg__sublab">KIG 3-5</div>
              </td>
              <td v-for="tarif in tarifs" :key="`kb3-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_kfo_beg_mgkv }}</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Leistungen</strong>
                <div class="dvg__sublab">Kinder-Zahnarzt</div>
              </td>
              <td v-for="tarif in tarifs" :key="`ks-${tarif.tarif_id}`">
                <ul class="dvg__list">
                  <li v-for="item in tarif.kindspezial" :key="item.id">
                    <span v-if="item.ja" class="dvg__list-ok">
                      {{ item.value }}
                    </span>
                    <span v-if="!item.ja" class="dvg__list-no">
                      {{ item.value }}
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
          </template>

          <template v-if="berechnung.ber_algo_view != 2">
            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Übersicht</span>
                <h3 class="dvg__section-title">
                  Leistungsübersicht der ersten Versicherungsjahre
                </h3>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Ø-Erstattung</strong>
                <div class="dvg__sublab">im 1.-4. Jahr</div>
              </td>
              <td v-for="tarif in tarifs" :key="`w4-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_wwert_4 }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Max. Erstattung</strong>
                <div class="dvg__sublab">Anfangsbegrenzung</div>
              </td>
              <td v-for="tarif in tarifs" :key="`b2-${tarif.tarif_id}`">
                <b>{{ begKurz(tarif) }}</b>
              </td>
            </tr>

            <tr>
              <td class="dvg__lab"><strong>Zahnersatz</strong></td>
              <td v-for="tarif in tarifs" :key="`ze-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_ze }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Zahnerhalt</strong></td>
              <td v-for="tarif in tarifs" :key="`zb-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_zb }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Prophylaxe</strong></td>
              <td v-for="tarif in tarifs" :key="`pz-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_pzr }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Hightech</strong>
                <div class="dvg__sublab">Zahnarzt-Leistungen</div>
              </td>
              <td v-for="tarif in tarifs" :key="`ht-${tarif.tarif_id}`">
                <ul class="dvg__list">
                  <li v-for="item in tarif.highspezial" :key="item.id">
                    <span v-if="item.ja" class="dvg__list-ok">
                      {{ item.value }}
                    </span>
                    <span v-if="!item.ja" class="dvg__list-no">
                      {{ item.value }}
                    </span>
                  </li>
                </ul>
              </td>
            </tr>

            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Detail</span>
                <h3 class="dvg__section-title">Leistungen für Zahnersatz</h3>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Zahnersatz</strong></td>
              <td v-for="tarif in tarifs" :key="`ze2-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_leistung_ze }}&nbsp;%</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Wartezeit</strong>
                <div class="dvg__sublab">für Zahnersatz</div>
              </td>
              <td v-for="tarif in tarifs" :key="`wz-${tarif.tarif_id}`">
                <b>{{ tarif.tarif_beschreibung_wz_ze }}</b>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Implantate</strong>
                <div class="dvg__sublab">Begr. Anzahl / Betrag</div>
              </td>
              <td v-for="tarif in tarifs" :key="`im-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_implantat }}</strong>
                <div class="dvg__hint">
                  {{ tarif.tarif_beschreibung_implantat_beg_anzahl }}
                </div>
                <div class="dvg__hint">
                  {{ tarif.tarif_beschreibung_implantat_beg_betrag }}
                </div>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Inlays</strong>
                <div class="dvg__sublab">Begrenzungen</div>
              </td>
              <td v-for="tarif in tarifs" :key="`in-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_inlay }}</strong>
                <div class="dvg__hint">
                  {{ tarif.tarif_beschreibung_inlay_beg }}
                </div>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Verblendungen</strong>
                <div class="dvg__sublab">Begrenzungen</div>
              </td>
              <td v-for="tarif in tarifs" :key="`vb-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_verblendung }}</strong>
                <div class="dvg__hint">
                  {{ tarif.tarif_beschreibung_verblendung_beg }}
                </div>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Knochenaufbau</strong></td>
              <td v-for="tarif in tarifs" :key="`kn-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_knochenaufbau }}</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Funktionsanalyse</strong></td>
              <td v-for="tarif in tarifs" :key="`fa-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_funktion_ze }}</strong>
              </td>
            </tr>

            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Detail</span>
                <h3 class="dvg__section-title">
                  Leistungen für zahnerhaltende Maßnahmen
                </h3>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Zahnerhalt</strong></td>
              <td v-for="tarif in tarifs" :key="`zb2-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_leistung_zb }}&nbsp;%</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Wartezeit</strong>
                <div class="dvg__sublab">für Zahnerhalt</div>
              </td>
              <td v-for="tarif in tarifs" :key="`wzb-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_wz_zb }}</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Kunststofffüllungen</strong></td>
              <td v-for="tarif in tarifs" :key="`ksf-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_ksf }}</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Wurzelbehandlung</strong>
                <div class="dvg__sublab">wenn GKV leistet</div>
                <div class="dvg__sublab dvg__sublab--neg">
                  wenn GKV nicht leistet
                </div>
              </td>
              <td v-for="tarif in tarifs" :key="`wu-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_wurzel_mgkv }}</strong>
                <div>{{ tarif.tarif_beschreibung_wurzel_ogkv }}</div>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Parodontose-Leistungen</strong>
                <div class="dvg__sublab">wenn GKV leistet</div>
                <div class="dvg__sublab dvg__sublab--neg">
                  wenn GKV nicht leistet
                </div>
              </td>
              <td v-for="tarif in tarifs" :key="`pa-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_paro_mgkv }}</strong>
                <div>{{ tarif.tarif_beschreibung_paro_ogkv }}</div>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Funktionsanalyse</strong>
                <div class="dvg__sublab">für Zahnerhalt</div>
              </td>
              <td v-for="tarif in tarifs" :key="`fz-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_funktion_zb }}</strong>
              </td>
            </tr>

            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Detail</span>
                <h3 class="dvg__section-title">Leistungen für Prophylaxe</h3>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Prophylaxe</strong>
                <div class="dvg__sublab">für Zahnerhalt</div>
              </td>
              <td v-for="tarif in tarifs" :key="`pz2-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_leistung_pzr }}&nbsp;%</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab">
                <strong>Wartezeit</strong>
                <div class="dvg__sublab">für Prophylaxe</div>
              </td>
              <td v-for="tarif in tarifs" :key="`pw-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_beschreibung_wz_pzr }}</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Details</strong></td>
              <td v-for="tarif in tarifs" :key="`pd-${tarif.tarif_id}`">
                <strong>{{ tarif.tarif_leistung_pzr_details }}</strong>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Bleaching</strong></td>
              <td v-for="tarif in tarifs" :key="`bl-${tarif.tarif_id}`">
                <span
                  v-if="!tarif.tarif_beschreibung_bleaching"
                  class="dvg__neg"
                >
                  keine Leistung
                </span>
                <span v-if="tarif.tarif_beschreibung_bleaching">
                  {{ tarif.tarif_beschreibung_bleaching }}
                </span>
              </td>
            </tr>

            <tr class="dvg__section">
              <td :colspan="showColspan">
                <span class="dvg__section-eyebrow">Konditionen</span>
                <h3 class="dvg__section-title">
                  Begrenzungen, Wartezeiten &amp; Kündigungsfristen
                </h3>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Begrenzungen</strong></td>
              <td v-for="tarif in tarifs" :key="`be-${tarif.tarif_id}`">
                <span
                  v-html="
                    tarif.tarif_beschreibung_beg.replace(/\n/g, '<br />')
                  "
                ></span>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Wartezeiten</strong></td>
              <td v-for="tarif in tarifs" :key="`wzz-${tarif.tarif_id}`">
                <span
                  v-html="
                    tarif1.tarif_modal_wartezeit.replace(/\n/g, '<br />')
                  "
                ></span>
              </td>
            </tr>
            <tr>
              <td class="dvg__lab"><strong>Kündigungsfrist</strong></td>
              <td v-for="tarif in tarifs" :key="`ku-${tarif.tarif_id}`">
                {{ tarif.tarif_int_kuendigung_details }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
  import { ref, onMounted, watch, reactive, computed, defineProps } from "vue";
  import { useSharedDataStore } from "@/stores/sharedData";
  import { useBegrenzung } from "@/composable/useBegrenzung";
  const { kurz: begKurz } = useBegrenzung();
  const sharedDataStore = useSharedDataStore();
  const props = defineProps(["tarif1", "alletarife", "berechnung"]);

  const alletarifeFiltered = ref([]);
  const showColspan = ref(2);

  const state = reactive({
    select: {
      tarif_id: props.tarif1.tarif_id,
      action: "beitragsentwicklung",
      berechnung: props.berechnung,
      tarif: props.tarif1,
    },
    tarif2: null,
    tarif3: null,
  });

  const tarifs = computed(() => {
    let tarifsArray = [props.tarif1];
    if (state.tarif2 !== null) tarifsArray.push(state.tarif2);
    if (state.tarif3 !== null) tarifsArray.push(state.tarif3);
    return tarifsArray;
  });

  onMounted(() => {
    filterAlle();
  });

  function filterAlle() {
    alletarifeFiltered.value = props.alletarife.filter(
      (x) => x.tarif_id !== parseInt(props.tarif1.tarif_id)
    );
  }

  function pushTarifToVergleich(event, tarif_nr) {
    if (tarif_nr === 2) {
      const treffer2 = props.alletarife.filter(
        (x) => parseInt(x.tarif_id) === parseInt(event.target.value)
      );
      state.tarif2 = treffer2[0] || null;
    } else if (tarif_nr === 3) {
      const treffer3 = props.alletarife.filter(
        (x) => parseInt(x.tarif_id) === parseInt(event.target.value)
      );
      state.tarif3 = treffer3[0] || null;
    }
  }

  watch(
    () => tarifs.value.length,
    (newLength) => {
      showColspan.value = newLength + 1;
    },
    { immediate: true }
  );
</script>

<style scoped>
  .dvg {
    padding: 12px 18px 24px;
    max-width: 1280px;
    margin: 0 auto;
  }

  .dvg__head {
    background: #ffffff;
    border: 1px solid #d8dde5;
    border-left: 3px solid #0f4c81;
    border-radius: 3px;
    padding: 12px 16px;
    margin-bottom: 14px;
  }
  .dvg__eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.2px;
    color: #5b6473;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .dvg__dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #0f4c81;
    margin-right: 8px;
  }
  .dvg__title {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 22px);
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
    line-height: 1.2;
  }
  .dvg__sub {
    margin: 6px 0 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.5;
  }

  .dvg__mobile {
    margin-bottom: 14px;
  }

  .dvg__scroll {
    width: 100%;
    overflow-x: auto;
    border: 1px solid #e5e9f0;
    border-radius: 3px;
    background: #ffffff;
  }

  .dvg__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: #1f2937;
    min-width: 720px;
  }
  .dvg__table th,
  .dvg__table td {
    padding: 10px 12px;
    vertical-align: top;
    border-bottom: 1px solid #eef1f6;
    text-align: left;
  }
  .dvg__table tbody tr:nth-child(even) td {
    background: #fbfcfe;
  }

  .dvg__th {
    background: #f5f7fb;
    border-bottom: 2px solid #0f4c81;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .dvg__th--label {
    width: 220px;
    font-weight: 700;
    color: #0f4c81;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    font-size: 11.5px;
  }
  .dvg__th--tarif {
    width: 33%;
    min-width: 220px;
  }

  .dvg__tcol {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .dvg__tcol-eyebrow {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #5b6473;
    text-transform: uppercase;
  }
  .dvg__tcol-name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.25;
  }
  .dvg__tcol-price {
    font-size: 16px;
    font-weight: 800;
    color: #0f4c81;
    letter-spacing: -0.3px;
    font-variant-numeric: tabular-nums;
  }
  .dvg__tcol-unit {
    font-size: 11px;
    color: #6b7280;
    font-weight: 600;
    margin-left: 2px;
  }
  .dvg__tcol--fix {
    border-bottom: 0;
  }

  .dvg__select {
    width: 100%;
    padding: 6px 8px;
    font-size: 13px;
    border: 1px solid #c8d0db;
    border-radius: 3px;
    background: #fff;
    color: #0f172a;
    font-weight: 600;
    outline: none;
  }
  .dvg__select:focus {
    border-color: #0f4c81;
    box-shadow: 0 0 0 2px rgba(15, 76, 129, 0.12);
  }

  .dvg__lab {
    width: 220px;
    background: #ffffff;
    border-right: 1px solid #eef1f6;
  }
  .dvg__lab strong {
    color: #0f172a;
    font-weight: 700;
  }
  .dvg__sublab {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 2px;
  }
  .dvg__sublab--neg {
    color: #b91c1c;
  }

  .dvg__price {
    font-size: 15px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.2px;
    font-variant-numeric: tabular-nums;
  }

  .dvg__badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: 3px;
    background: #f0f5fa;
    border: 1px solid #d8e3ee;
    color: #0f4c81;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .dvg__badge--rec {
    background: #fff5cc;
    border-color: #ffe58f;
    color: #8a6d00;
  }

  .dvg__hint {
    font-size: 11.5px;
    color: #6b7280;
    margin-top: 3px;
  }

  .dvg__cta {
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: none;
    color: #fff !important;
  }
  .dvg__cta-tip {
    margin-left: 6px;
    padding: 1px 6px;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  .dvg__pos {
    color: #2e7d32;
  }
  .dvg__neg {
    color: #b91c1c;
  }

  .dvg__section td {
    background: #f7faff !important;
    border-top: 2px solid #0f4c81;
    border-bottom: 1px solid #d8e3ee;
    padding: 10px 12px !important;
  }
  .dvg__section-eyebrow {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.1px;
    color: #0f4c81;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
  .dvg__section-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.1px;
  }

  .dvg__list {
    list-style: disc outside;
    margin: 0;
    padding-left: 18px;
  }
  .dvg__list li::marker {
    color: #0f4c81;
  }
  .dvg__list li {
    margin: 1px 0;
    font-size: 12.5px;
  }
  .dvg__list-no {
    color: #b91c1c;
    text-decoration: line-through;
  }
  .dvg__list-ok {
    color: #1f2937;
  }

  @media (max-width: 720px) {
    .dvg {
      padding: 8px 10px 18px;
    }
    .dvg__th--label,
    .dvg__lab {
      width: 160px;
      min-width: 160px;
    }
  }
</style>
